/*
 * The Patron page.
 *
 * An asset rather than a script inside the section, because the theme does not
 * re-run a section's inline script after an SPA swap. It only re-executes
 * scripts for lazily inserted homepage sections; an ordinary page change
 * inserts the markup and leaves the script dead. The styles survive, which is
 * why the page looked right and did nothing: no sale price, no billing toggle,
 * no AJAX cart, and — worst of it — the upgrade forms falling back to posting
 * to /cart/add, which is the second subscription contract all over again.
 *
 * So it initialises the way everything else in this theme that must survive a
 * transition does: on load, on od:after-swap, and on pushstate and popstate.
 */
(function () {
  "use strict";

  function init() {
    var root = document.querySelector('[data-od-patron]');
    if (!root) return false;
    if (root.dataset.odBound === '1') return true;
    root.dataset.odBound = '1';

    var options = root.querySelectorAll('[data-od-interval]');
    var annualForm = root.querySelector('[data-od-form-annual]');
    var monthlyForm = root.querySelector('[data-od-form-monthly]');

    var annualPrice = root.querySelector('[data-od-price-annual]');
    var monthlyPrice = root.querySelector('[data-od-price-monthly]');

    function choose(interval) {
      for (var i = 0; i < options.length; i++) {
        options[i].setAttribute(
          'aria-pressed',
          options[i].dataset.odInterval === interval ? 'true' : 'false'
        );
      }
      if (annualForm) annualForm.hidden = interval !== 'annual';
      if (monthlyForm) monthlyForm.hidden = interval !== 'monthly';
      if (annualPrice) annualPrice.hidden = interval !== 'annual';
      if (monthlyPrice) monthlyPrice.hidden = interval !== 'monthly';
    }

    /* A running membership sale, on the yearly price only, read from the sale
       rather than written into the page so one starting or ending needs no
       republish. The same call and the same wording as the plans page. */
    /*
     * Learned from a price Liquid already rendered on this page, so a rewritten
     * price is formatted exactly like the one struck through beside it. The
     * store sells into 237 countries through the currency picker, and a
     * hardcoded dollar sign printed the right number with the wrong symbol for
     * every one of them but the US.
     *
     * Learn before rewriting anything: the element it reads is the element it
     * is about to overwrite.
     */
    function money(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    function moneyFor(card) {
      var amount = card && card.querySelector('[data-od-amount]');
      var cents = card && card.dataset ? card.dataset.annualCents : null;
      var learned =
        window.ODMoney && amount ? window.ODMoney.learn(cents, amount.textContent) : null;
      return learned || money;
    }

    var salePercentage = null;

    /* Set by bindUpgrade, called when the contract turns out to be cancelled. */
    var leaveUpgradeMode = null;

    function showIntroPrice(percentage) {
      /* A membership sale is a first-cycle pricing policy on the selling plan,
         and a tier change never touches one. Quoting a first-year price to
         somebody upgrading would promise a discount they will not get: they
         renew at the standing Patron rate. */
      if (root.dataset.odUpgradeFrom) return;

      var card = root.querySelector('[data-annual-cents]');
      if (!card || !annualPrice) return;

      var annual = Number(card.dataset.annualCents);
      if (!isFinite(annual) || annual <= 0) return;

      var money = moneyFor(annualPrice.closest ? annualPrice.closest('[data-annual-cents]') || card : card);

      var first = Math.round(annual * (100 - percentage) / 100);
      if (first >= annual) return;

      var was = annualPrice.querySelector('[data-od-was]');
      var amount = annualPrice.querySelector('[data-od-amount]');
      var per = annualPrice.querySelector('[data-od-per]');
      var then = annualPrice.querySelector('[data-od-intro]');
      if (!was || !amount || !per || !then) return;

      /* The sale price becomes the headline with the standing price struck
         above it: leaving the standing price large puts the eye on the number
         nobody pays. */
      was.textContent = money(annual);
      was.hidden = false;
      amount.textContent = money(first);
      per.textContent = 'for your first year';

      /* Both totals stay on the page. The discount covers the first year only,
         so the second year must never be a surprise. */
      then.innerHTML = 'Then <strong>' + money(annual) + ' a year</strong>. Cancel any time.';
      then.hidden = false;
    }

    fetch('/apps/library/active-sale', { credentials: 'same-origin' })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        /* Only the membership sale, never the storewide one: a storewide
           percentage cannot reach a subscription, so quoting it here would
           promise a saving no charge could honour. */
        var sale = data && data.membershipSale;
        var value = Number(sale && sale.percentage);
        if (!isFinite(value) || value <= 0 || value >= 100) return;

        /* Kept, because an upgrade ignores it but a cancelled member falling
           back to buying is a new subscription and does get it. */
        salePercentage = value;
        showIntroPrice(value);
      })
      .catch(function () {
        /* The standing price is always correct, so failing quietly undersells
           rather than misquotes. */
      });

    for (var i = 0; i < options.length; i++) {
      options[i].addEventListener('click', function (event) {
        choose(event.currentTarget.dataset.odInterval);
      });
    }

    /* Add to the cart the way the rest of the theme does.
       A native form post to /cart/add is a full page load, which on an SPA
       theme is the one thing the whole journey avoids. Alpine.store("cart")
       .addToCart posts to cart/add.js, refreshes the mini cart and leaves the
       page where it is: the same call snippets/product-actions-button.liquid
       makes. Falls back to submitting the form if the store is not there, so
       the worst case is the page load we had before rather than a dead
       button. */
    function bindCartForms(scope) {
      var forms = scope.querySelectorAll('[data-od-form-monthly], [data-od-form-annual]');

      for (var i = 0; i < forms.length; i++) {
        /* Claimed, so the cart guard's fallback leaves it alone: this page is
           handling the submit and the guard must not act on it as well. */
        forms[i].dataset.odCartBound = '1';

        forms[i].addEventListener('submit', function (event) {
          var form = event.currentTarget;
          var store = window.Alpine && window.Alpine.store && window.Alpine.store('cart');
          if (!store || typeof store.addToCart !== 'function') return;

          event.preventDefault();

          var props = {};
          var data = new FormData(form);
          data.forEach(function (value, key) { props[key] = value; });

          var button = form.querySelector('button[type="submit"]');

          /* The theme swaps the label rather than showing a spinner, which is
             what snippets/product-actions-button.liquid does, so this reads as
             the same shop rather than as a page somebody bolted on. */
          if (button) {
            button.disabled = true;
            if (button.dataset.odAddingLabel) {
              button.textContent = button.dataset.odAddingLabel;
            }
          }

          function restore() {
            if (!button) return;
            button.disabled = false;
            if (button.dataset.odLabel) button.textContent = button.dataset.odLabel;
          }

          try {
            store.addToCart(props);
          } catch (error) {
            restore();
            form.submit();
            return;
          }

          /* The store tracks its own in-flight ids and opens the mini cart on
             success; this only gives the button back, so a second membership
             can be chosen if they change their mind. */
          window.setTimeout(restore, 1200);
        });
      }
    }

    /*
     * A Member or a Collector never reaches the cart. Adding the Patron product
     * would open a second subscription contract beside the one they already pay
     * for and bill them for both; change-tier moves the contract they hold. The
     * page is otherwise untouched: the same card, the same price, the same
     * button, asking first exactly as the membership page does.
     */
    var upgradeFrom = root.dataset.odUpgradeFrom || '';

    if (upgradeFrom) {
      bindUpgrade(root);
    } else {
      bindCartForms(root);
    }

    function bindUpgrade(scope) {
      var dialog = root.querySelector('[data-od-dialog]');
      var forms = scope.querySelectorAll('[data-od-form-monthly], [data-od-form-annual]');
      if (!dialog || !forms.length) return;

      /*
       * Out to the body. A transformed ancestor becomes the containing block
       * for position: fixed, so inside the section the dialog centred itself
       * within the card and sat half under the header.
       */
      var stale = document.getElementById(dialog.id);
      if (stale && stale !== dialog && stale.parentNode === document.body) {
        stale.parentNode.removeChild(stale);
      }
      document.body.appendChild(dialog);

      /* The theme swaps the page without unloading it, and a dialog left at the
         body would outlive the section it belongs to. */
      var onEscape = function (event) {
        if (event.key === 'Escape') closeDialog();
      };

      var forget = function () {
        if (document.body.contains(root)) return;
        if (dialog.parentNode) dialog.parentNode.removeChild(dialog);
        window.removeEventListener('pushstate', forget);
        window.removeEventListener('popstate', forget);
        document.removeEventListener('keydown', onEscape);
      };
      window.addEventListener('pushstate', forget);
      window.addEventListener('popstate', forget);

      var upgradeMode = true;
      var dialogGo = dialog.querySelector('[data-od-dialog-go]');
      var result = document.createElement('p');
      result.className = 'od-patron__upgrade-result';
      result.hidden = true;

      var buttons = [];

      for (var i = 0; i < forms.length; i++) {
        var button = forms[i].querySelector('button');
        if (!button) continue;
        /* Same claim as bindCartForms. Without it the guard saw an unhandled
           membership submit, waited a tick, and added Patron to the cart while
           the confirmation dialog was still on screen — an upgrade turned into
           a purchase. */
        forms[i].dataset.odCartBound = '1';

        button.dataset.odBuyLabel = button.dataset.odLabel || button.textContent;
        button.dataset.odLabel = 'Upgrade to Patron';
        button.textContent = 'Upgrade to Patron';
        buttons.push(button);

        forms[i].addEventListener('submit', function (event) {
          /* Bound before the cart's own handler, so standing aside here lets
             the ordinary add-to-cart run. */
          if (!upgradeMode) return;
          event.preventDefault();
          dialog.hidden = false;
          void dialog.offsetWidth;
          dialog.classList.add('is-open');
          if (dialogGo) dialogGo.focus();
        });
      }

      if (buttons.length) {
        var last = buttons[buttons.length - 1];
        if (last.parentNode && last.parentNode.parentNode) {
          last.parentNode.parentNode.appendChild(result);
        }
      }

      function closeDialog() {
        if (dialog.hidden) return;
        dialog.classList.remove('is-open');
        window.setTimeout(function () {
          if (!dialog.classList.contains('is-open')) dialog.hidden = true;
        }, 200);
      }

      dialog.addEventListener('click', function (event) {
        if (event.target.hasAttribute('data-od-dialog-close')) closeDialog();
      });

      document.addEventListener('keydown', onEscape);

      /*
       * A cancelled contract is finished: there is nothing left to move, so the
       * upgrade would fail on press. Starting again is the only thing that
       * works, and the page already has the card to do it with.
       */
      leaveUpgradeMode = function () {
        upgradeMode = false;
        delete root.dataset.odUpgradeFrom;

        if (dialog.parentNode) dialog.parentNode.removeChild(dialog);
        document.removeEventListener('keydown', onEscape);

        for (var b = 0; b < buttons.length; b++) {
          buttons[b].disabled = false;
          buttons[b].textContent = buttons[b].dataset.odBuyLabel || 'Start your Patron membership';
          buttons[b].dataset.odLabel = buttons[b].textContent;
        }

        /* Buying is a new subscription, so the period is theirs to choose
           again and the sale applies to it. */
        var toggle = root.querySelector('.od-patron__switch');
        if (toggle) toggle.hidden = false;
        choose('annual');

        bindCartForms(root);
        if (salePercentage !== null) showIntroPrice(salePercentage);
      };

      function setButtons(text, disabled) {
        for (var b = 0; b < buttons.length; b++) {
          buttons[b].textContent = text;
          buttons[b].disabled = disabled;
        }
      }

      /* A refusal for want of a card is the one failure the member can do
         something about, so it carries the way to do it. */
      function fail(message, data) {
        setButtons('Upgrade to Patron', false);
        result.className = 'od-patron__upgrade-result is-error';
        result.textContent = message;

        var cardUrl = (root.getAttribute('data-od-card-url') || '').trim();
        if (cardUrl && data && data.code === 'no_payment_method') {
          result.appendChild(document.createTextNode(' '));
          var anchor = document.createElement('a');
          anchor.href = cardUrl;
          anchor.textContent = 'Add a card to your account';
          result.appendChild(anchor);
        }

        result.hidden = false;
      }

      if (!dialogGo) return;

      dialogGo.addEventListener('click', function () {
        closeDialog();
        setButtons('Upgrading...', true);

        fetch('/apps/library/membership/change-tier', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ tier: 'patron' })
        })
          .then(function (response) {
            return response.json()
              .catch(function () { return {}; })
              .then(function (data) { return { status: response.status, data: data }; });
          })
          .then(function (res) {
            if (res.status === 401) {
              fail('Please sign in again to change your membership.');
              return;
            }

            if (!res.data || res.data.ok !== true) {
              fail(
                (res.data && res.data.error) || 'We could not change your membership just now. Please try again, or write to us at info@operadepot.com.',
                res.data
              );
              return;
            }

            /* The gate keeps the tier in session storage, and the recording it
               would not play is the reason they are here. Told now rather than
               after the reload, so a new Patron is not offered Patron. */
            try {
              sessionStorage.removeItem('od-member-tier-v1');
              sessionStorage.removeItem('od-active-sale-v3');
            } catch (error) {
              /* private mode: nothing was cached to begin with */
            }

            try {
              window.dispatchEvent(
                new CustomEvent('od:membership-changed', { detail: { tier: 'patron' } })
              );
            } catch (error) {
              /* never worth failing a completed change over */
            }

            setButtons('You are a Patron', true);
            result.className = 'od-patron__upgrade-result';
            result.textContent = 'You are a Patron. The Coming Soon collection is open to you now.';
            result.hidden = false;

            /* Liquid rendered this page for a Collector; only a reload can
               render it honestly for the Patron they now are. */
            window.setTimeout(function () { window.location.reload(); }, 2400);
          })
          .catch(function () {
            fail('Something went wrong reaching us. Please check your connection and try again.');
          });
      });
    }

    /* The toggle is hidden for an upgrade, so this is the only thing choosing,
       and it picks the period their membership already bills on. */
    choose(root.dataset.odUpgradeInterval || 'annual');

    /*
     * A Patron whose membership is already on its way out.
     *
     * Liquid reads the tier metafield, which still says patron: cancelling
     * stops billing and leaves access running to the end of the paid period
     * and writes nothing to the customer record. So this page thanked somebody
     * for support they had just withdrawn.
     *
     * The contract is the only thing that knows, and the guarantee call
     * reports it.
     */
    var alreadyText = root.querySelector('[data-od-already-text]');

    /*
     * Asked for every signed-in visitor, not only for a Patron or an upgrader.
     *
     * The guarantee is twice per customer and it outlives the membership it was
     * spent on. Somebody who joined, was refunded twice and has since lapsed
     * reads as a brand new visitor in Liquid — no tier, no contract — and is
     * shown the buy card and the promise above it. They will not get a third
     * refund.
     *
     * The condition used to be "a Patron note or an upgrader exists", which
     * skipped the plain non-member: the page's main audience, and the only one
     * the promise is actually addressed to.
     */
    if (window.ODCustomerSignedIn) {
      /* Held back while we ask, so a customer who cannot have the refund never
         reads that they can. A visitor we have never met waits for nothing. */
      root.classList.add('is-checking');

      var guaranteeSettled = false;
      var settleGuarantee = function () {
        if (guaranteeSettled) return;
        guaranteeSettled = true;
        root.classList.remove('is-checking');
      };

      window.setTimeout(settleGuarantee, 4000);

      fetch('/apps/library/membership/guarantee', { credentials: 'same-origin' })
        .then(function (response) { return response.json(); })
        .then(function (data) {
          if (!data) return;

          if (data.guaranteeAvailable === false) {
            /* Withheld rather than removed. Hiding it outright collapsed the
               space it had been holding and the card jumped up the page, which
               is the shift the waiting was meant to avoid. The words go; the
               room they took stays. */
            var promise = root.querySelector('.od-patron__guarantee');
            if (promise) promise.classList.add('is-withheld');
          }

          if (!alreadyText && !leaveUpgradeMode) return;
          if (!data.membership) return;
          if (data.membership.status !== 'cancelled') return;

          var when = '';
          /* Same rule as the letters: a date already gone is no date. */
          if (
            data.membership.accessEndsAt &&
            Date.parse(data.membership.accessEndsAt) > Date.now()
          ) {
            var parsed = new Date(data.membership.accessEndsAt);
            if (!isNaN(parsed.getTime())) {
              when = parsed.toLocaleDateString(undefined, {
                year: 'numeric', month: 'long', day: 'numeric'
              });
            }
          }

          if (leaveUpgradeMode) leaveUpgradeMode();

          if (!alreadyText) {
            /* A cancelled Member or Collector is told where they stand, above
               the card they can now use. */
            var lead = document.createElement('p');
            lead.className = 'od-patron__upgrade-result';
            lead.textContent = when
              ? 'Your membership is cancelled and your access ends on ' + when + '. A cancelled membership cannot be changed, so becoming a Patron means starting again.'
              : 'Your membership is cancelled. A cancelled membership cannot be changed, so becoming a Patron means starting again.';

            var card = root.querySelector('.od-patron__card');
            if (card && card.parentNode) card.parentNode.insertBefore(lead, card);

            return;
          }

          alreadyText.textContent = when
            ? 'You have cancelled your subscription. Your benefits will expire on ' + when + '.'
            : 'You have cancelled your subscription. Your benefits will expire at the end of the period you have paid for.';

          /* And show them the way back. Starting again before the old access
             ends means paying for time they already hold, which is their
             decision to make: Andy's call, and the alternative is a page that
             tells somebody their membership is ending and offers no way to
             keep it. */
          var purchase = root.querySelector('[data-od-purchase]');
          if (purchase) purchase.hidden = false;

          var again = root.querySelector('[data-od-again]');
          if (again) again.hidden = false;

          /* The pitch to become a Patron reads oddly directly above a notice
             saying their Patron membership is ending. The heading and the card
             carry the offer on their own. */
          var intro = root.querySelector('[data-od-intro-copy]');
          if (intro) intro.hidden = true;

        })
        .catch(function () {
          /* The ordinary line stands. Telling somebody their membership has
             ended because a request failed would be worse than thanking a
             leaver. */
        })
        .then(settleGuarantee, settleGuarantee);
    }

    return true;
  }

  /* Somebody can become a Patron in another tab, or through the app, while
     this page is open. Asking again when the page comes back into view is
     cheaper than letting them buy a membership they already hold. */
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState !== 'visible') return;
    if (typeof window.ODRefreshMembershipTier !== 'function') return;

    window.ODRefreshMembershipTier().then(function (tier) {
      if (String(tier || '').toLowerCase() !== 'patron') return;
      /* Liquid rendered the sales pitch; only a reload can render the "you
         are already a Patron" branch honestly. */
      window.location.reload();
    });
  });

  /*
   * Watching the document, not counting the seconds.
   *
   * The theme dispatches pushstate when the link is pressed, before it has
   * fetched the new page, and od:after-swap does not fire on every route. A
   * window of retries after pushstate therefore expires while the old page is
   * still on screen, and nothing runs again once the new markup lands — the
   * page arrives looking finished and does nothing, which is exactly the bug
   * this file was created to fix.
   *
   * An observer has no window to get wrong. It is also what od-sale-display.js
   * does, and the reason a product page keeps its sale price across a
   * transition while this page lost everything.
   */
  var scheduled = false;

  function schedule() {
    if (scheduled) return;
    scheduled = true;

    /*
     * A frame or a timer, whichever comes first.
     *
     * requestAnimationFrame does not fire in a background tab, and the flag
     * above latches while one is pending, so a page opened in a tab nobody is
     * looking at never initialised: the billing toggle dead, the cart forms
     * unclaimed, and the refund promise on screen without having been checked.
     * It recovered when the tab was focused, which is late.
     */
    var run = function () {
      if (!scheduled) return;
      scheduled = false;
      init();
    };

    window.requestAnimationFrame(run);
    window.setTimeout(run, 50);
  }

  schedule();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", schedule);
  }

  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  /* Kept as well: a swap that replaces nothing this observer can see still
     announces itself. init() is guarded, so running twice costs a querySelector. */
  document.addEventListener("od:after-swap", schedule);
  window.addEventListener("pushstate", schedule);
  window.addEventListener("popstate", schedule);
})();
